import React from 'react';
import LinkNoPrefetch from '../linkNoPrefetch/LinkNoPrefetch';
import styled, { css } from 'styled-components';
import { typography } from '../../theme';
import { colors } from '../../theme/color';
import { LoadingIconCoupon } from '../loading/LoadingIconCoupon';

export type buttonGrid =
  | 'default'
  | 'autodetect'
  | '50x50'
  | 'IconText'
  | 'IconTextIcon'
  | 'IconTextCentered'
  | 'IconTextText';

export enum buttonType {
  PRIMARY = 'primary',
  INVERTED = 'inverted',
  SECONDARY = 'secondary',
  SECONDARY_OUTLINE = 'secondary-outline',
  TERTIARY = 'tertiary',
  OUTLINE = 'outline',
  LINK = 'link',
}

type BaseProps = {
  buttonTheme?: buttonType;
  disabled?: boolean;
  className?: string;
  grid?: buttonGrid;
  alignItems?: 'center' | 'start' | 'end';
  width?: 'text' | 'medium' | 'full';
  active?: boolean;
  testId?: string;
  buttonHtmlType?: 'button' | 'submit' | 'reset';
  name?: string;
  loading?: boolean;
  children?: React.ReactNode;
};

export type ButtonProps = BaseProps & {
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

type LinkProps = BaseProps & {
  href: string;
};

type RouterLinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  prefetch?: 'intent' | 'render' | 'none' | 'viewport';
  reloadDocument?: boolean;
  replace?: boolean;
  preventScrollReset?: boolean;
  to: string;
  viewTransition?: boolean;
} & BaseProps;

export type Props = ButtonProps | LinkProps | BaseProps | RouterLinkProps;

const isLink = (props: Props): props is LinkProps => {
  return Boolean((props as LinkProps).href);
};

const isReactRouterLink = (props: Props): props is RouterLinkProps => {
  return Boolean((props as RouterLinkProps).to);
};

const isButton = (props: Props): props is ButtonProps => {
  return Boolean((props as ButtonProps).onClick) && !isLink(props) && !isReactRouterLink(props);
};

const isSubmit = (props: Props): props is ButtonProps => {
  return props.buttonHtmlType === 'submit';
};

const getGrid = (grid: buttonGrid | undefined, childrenCount: number): buttonGrid => {
  if (grid && grid !== 'autodetect') {
    return grid;
  }

  switch (childrenCount) {
    case 3:
      return 'IconTextIcon';
    case 2:
      return 'IconText';
    case 1:
    default:
      return 'default';
  }
};

export const Button: React.FC<Props> = ({
  children,
  grid,
  disabled = false,
  loading = false,
  ...props
}) => {
  children = ([] as React.ReactNode[]).concat(children).filter(Boolean);

  if (isButton(props)) {
    const onClick = props.onClick;
    props.onClick = (e) => {
      e.stopPropagation();
      e.preventDefault();

      if (!disabled) {
        onClick(e);
      }
    };
  }

  if (loading) {
    children = <LoadingIconCoupon />;
  }

  if (Array.isArray(children)) {
    children = children.map((child, i) =>
      typeof child === 'string' ? <span key={`button-child-${i}`}>{child}</span> : child
    );
  } else if (typeof children === 'string') {
    children = <span>{children}</span>;
  }

  return (
    <BaseComponent
      grid={getGrid(grid, Array.isArray(children) ? children.length : 1)}
      disabled={disabled || loading}
      {...props}
    >
      {children}
    </BaseComponent>
  );
};

export const Primary: React.FC<Props> = ({ children, ...props }) => (
  <Button {...props} buttonTheme={buttonType.PRIMARY}>
    {children}
  </Button>
);

export const Inverted: React.FC<Props> = ({ children, ...props }) => (
  <Button {...props} buttonTheme={buttonType.INVERTED}>
    {children}
  </Button>
);

export const Secondary: React.FC<Props> = ({ children, ...props }) => (
  <Button {...props} buttonTheme={buttonType.SECONDARY}>
    {children}
  </Button>
);

export const SecondaryOutline: React.FC<Props> = ({ children, ...props }) => (
  <Button {...props} buttonTheme={buttonType.SECONDARY_OUTLINE}>
    {children}
  </Button>
);

export const Tertiary: React.FC<Props> = ({ children, ...props }) => (
  <Button {...props} buttonTheme={buttonType.TERTIARY}>
    {children}
  </Button>
);

export const Outline: React.FC<Props> = ({ children, ...props }) => (
  <Button {...props} buttonTheme={buttonType.OUTLINE}>
    {children}
  </Button>
);

export const LinkButton: React.FC<Props> = ({ children, ...props }) => (
  <Button {...props} buttonTheme={buttonType.LINK}>
    {children}
  </Button>
);

const BaseComponent: React.FC<Props> = ({
  children,
  grid,
  alignItems,
  testId,
  buttonTheme,
  ...props
}) => {
  if (isLink(props)) {
    return (
      <StyledButton as="a" {...props} $buttonTheme={buttonTheme} data-testid={testId}>
        <Grid grid={grid} alignItems={alignItems}>
          {children}
        </Grid>
      </StyledButton>
    );
  } else if (isReactRouterLink(props)) {
    return (
      <StyledLink
        className={props.className}
        href={props.to}
        $buttonTheme={buttonTheme}
        data-testid={testId}
      >
        <Grid grid={grid} alignItems={alignItems}>
          {children}
        </Grid>
      </StyledLink>
    );
  } else if (isButton(props) || isSubmit(props)) {
    return (
      <StyledButton
        as="button"
        {...props}
        $buttonTheme={buttonTheme}
        data-testid={testId}
        type={props.buttonHtmlType}
      >
        <Grid grid={grid} alignItems={alignItems}>
          {children}
        </Grid>
      </StyledButton>
    );
  } else {
    return (
      <StyledButton {...props} $buttonTheme={buttonTheme} data-testid={testId}>
        <Grid grid={grid} alignItems={alignItems}>
          {children}
        </Grid>
      </StyledButton>
    );
  }
};

const primaryStyle = css`
  background-color: ${({ theme }) => theme.color?.button.primary};
  border: 2px solid ${({ theme }) => theme.color?.button.primary};
  color: ${({ theme }) => theme.color?.button.text};

  &:hover,
  &:focus-visible {
    background-color: ${({ theme }) => theme.isApp ? theme.color?.button.primary : colors.green.darker(30)};
    border-color: ${({ theme }) => theme.isApp ? theme.color?.button.primary : colors.green.darker(30)};
    color: ${({ theme }) => theme.isApp ? theme.color?.button.text : colors.textWhite};
  }
`;

const invertedStyle = css`
  background-color: ${colors.white};
  border: 2px solid ${colors.white};
  color: ${colors.textSuccess};

  &:hover,
  &:focus-visible {
    background-color: ${({ theme }) => theme.isApp ? colors.white : colors.textSuccess};
    border-color: ${({ theme }) => theme.isApp ? colors.white : colors.textSuccess};
    color: ${({ theme }) => theme.isApp ? colors.textSuccess : colors.textWhite};
  }
`;

const secondaryStyle = css`
  background-color: transparent;
  border: 2px solid ${colors.textSuccess};
  color: ${colors.textSuccess};

  &:hover,
  &:focus-visible {
    background-color: ${({ theme }) => theme.isApp ? 'transparent' : colors.green.darker(20)};
    border-color: ${({ theme }) => theme.isApp ? colors.textSuccess : colors.green.darker(20)};
    color: ${({ theme }) => theme.isApp ? colors.textSuccess : colors.textWhite};
  }
`;

const secondaryOutlineStyle = css`
  background-color: transparent;
  border: 2px solid ${colors.black};
  color: ${colors.black};
  font-family: ${({ theme }) => theme.fontFamily?.heading};
  font-weight: 700;

  &:hover,
  &:focus-visible {
    background-color: ${({ theme }) => theme.isApp ? 'transparent' : colors.white};
    border-color: ${({ theme }) => theme.isApp ? colors.black : colors.white};
    color: ${({ theme }) => theme.isApp ? colors.black : colors.green.darker(10)};
  }
`;

const tertiaryStyle = css`
  background-color: white;
  border: 2px solid ${colors.black};
  color: ${colors.black};

  &:hover,
  &:focus-visible {
    background-color: ${({ theme }) => theme.isApp ? 'white' : colors.green.darker(20)};
    border-color: ${({ theme }) => theme.isApp ? colors.black : colors.green.darker(20)};
    color: ${({ theme }) => theme.isApp ? colors.black : colors.textWhite};
  }
`;

const outlineStyle = css`
  ${invertedStyle}
  border: 2px solid ${colors.textSuccess};

  &:hover,
  &:focus-visible {
    border-color: ${colors.textSuccess};
  }
`;

const linkStyle = css`
  background: none;
  color: ${colors.green.darker(10)};
  border: none;
  text-decoration: underline;
  text-underline-position: under;

  &:hover {
    color: ${({ theme }) => theme.isApp ? colors.green.darker(10) : colors.green.darker(30)};
  }

  &:focus-visible {
    color: ${({ theme }) => theme.isApp ? colors.green.darker(10) : colors.green.darker(30)};
    outline: ${({ theme }) => theme.isApp ? 'none' : `2px solid ${colors.green.darker(30)} !important`};
  }
`;

const disabledStyle = css`
  filter: contrast(0.3) brightness(1.4);
  pointer-events: none;
`;

type GridProps = {
  grid: buttonGrid | undefined;
  alignItems?: 'center' | 'start' | 'end';
};

const Grid = styled.div<GridProps>`
  display: grid;
  width: 100%;
  align-items: ${({ alignItems }) => alignItems || 'center'};
  gap: 8px;

  ${({ grid }) => {
    switch (grid) {
      case 'IconTextIcon':
        return css`
          grid-template-columns: auto 1fr auto;
          text-align: left;

          *:nth-child(3) {
            justify-self: flex-end;
          }
        `;
      case 'IconTextCentered':
        return css`
          grid-template-columns: 1fr auto 1fr;
          text-align: center;
          justify-content: center;
          margin: auto;
          width: auto;

          *:nth-child(3) {
            justify-self: flex-end;
          }
        `;
      case 'IconText':
        return css`
          grid-template-columns: auto 1fr;
          text-align: left;

          *:nth-child(2) {
            justify-self: flex-start;
          }
        `;
      case 'IconTextText':
        return css`
          grid-template-columns: auto 1fr auto;
          text-align: left;
        `;
      case '50x50':
        return css`
          grid-template-columns: calc(50% - 8px) calc(50% - 8px);
          gap: 16px;

          *:first-child {
            justify-self: flex-start;
          }

          *:nth-child(2) {
            justify-self: flex-end;
          }
        `;
      default:
        return css`
          grid-template-columns: 100%;
          justify-items: center;
        `;
    }
  }}
`;

type StyledButtonProps = Props & {
  $buttonTheme?: buttonType;
};

const buttonStyles = css<StyledButtonProps>`
  display: grid;
  box-sizing: border-box;

  ${({ width }) => {
    switch (width) {
      case 'text':
        return '';
      case 'medium':
        return css`
          max-width: 20.4rem;
          width: 100%;
        `;
      case 'full':
      default:
        return css`
          max-width: 100%;
          width: 100%;
        `;
    }
  }};

  text-decoration: none;
  align-items: center;
  border-radius: ${({ theme }) => theme.sizes?.borderRadius.round};
  text-transform: initial;
  cursor: pointer;
  padding: 12px 16px;
  white-space: nowrap;

  transition: all 0.1s ease-in;

  ${({ $buttonTheme }) => {
    switch ($buttonTheme) {
      case 'inverted':
        return invertedStyle;
      case 'secondary':
        return secondaryStyle;
      case 'secondary-outline':
        return secondaryOutlineStyle;
      case 'tertiary':
        return tertiaryStyle;
      case 'outline':
        return outlineStyle;
      case 'link':
        return linkStyle;
      case 'primary':
      default:
        return primaryStyle;
    }
  }}

  svg {
    fill: currentColor;
  }

  ${({ disabled }) => disabled && disabledStyle}

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: ${({ theme }) => theme.isApp ? 'none' : `2px solid ${colors.green.darker(60)}`};
    outline-offset: 2px;
  }
`;

const StyledButton = styled.span<StyledButtonProps>`
  ${typography.buttonStyle}
  ${buttonStyles}
`;

const StyledLink = styled(LinkNoPrefetch)<StyledButtonProps>`
  ${typography.buttonStyle}
  ${buttonStyles}
`;
